import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import * as brands from "brands"
import * as api from "../api"
import * as routing from "../routing"
import * as v2layouts from "../layouts"
import * as cache from "../cache"
import * as authzc from "authzcached"
import * as icons from "icons"
import Container from "../container"
import { SubNavigation } from "../subnav"
import { AssetGrid } from "./grid"
import { BulkActions } from "./actions"
import { ConfirmationModal } from "./confirmation"
import { TrainingModal } from "./training"

export default function ReviewContainer(): JSX.Element {
	const { documentId } = useParams<{ documentId: string }>()
	const cached = cache.useCached()
	const brand = brands.caching.useCached()
	const metaauthz = authzc.useCache((cached) => cached.meta)

	const [document, setDocument] = useState<api.DocumentExtract | null>(null)
	const [assets, setAssets] = useState<api.AssetExtract[]>([])
	const [filters] = useState({
		assetType: "all",
		reviewStatus: "pending",
		search: "",
	})
	const [loading, setLoading] = useState(true)
	const [currentPage, setCurrentPage] = useState(1)
	const [totalCount, setTotalCount] = useState(0)
	const pageSize = 50
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [confirmModalData, setConfirmModalData] = useState<{
		message: string
		onConfirm: () => void
	} | null>(null)
	const [showTrainingModal, setShowTrainingModal] = useState(false)

	const loadDocument = async (): Promise<void> => {
		if (!documentId) return

		try {
			const docs = cached.documents.filter((d) => d.id === documentId)
			if (docs.length > 0) {
				setDocument(docs[0])
			}
		} catch (err) {
			console.error("unable to load document", err)
		}
	}

	const loadAssets = async (): Promise<void> => {
		if (!documentId) return

		setLoading(true)
		try {
			const offset = (currentPage - 1) * pageSize
			const req = api.zeros.assets.searchZero({
				document_id: documentId,
				asset_type: filters.assetType === "all" ? "" : filters.assetType,
				review_status: filters.reviewStatus === "all" ? "" : filters.reviewStatus,
				limit: pageSize,
				offset: offset,
			})

			const response = await api.assets.search(brand.id, documentId, req, authzc.bearer(metaauthz))
			setAssets(response.assets || [])
			setTotalCount(response.total_count || 0)
		} catch (err) {
			console.error("unable to load assets", err)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		loadDocument()
		cached.refreshDocuments()
	}, [documentId])

	useEffect(() => {
		setCurrentPage(1) // Reset to page 1 when filters change
	}, [documentId, filters])

	useEffect(() => {
		loadAssets()
	}, [documentId, filters, currentPage])

	const handleApprove = async (assetId: string): Promise<void> => {
		if (!documentId) return
		try {
			const response = await api.assets.approve(brand.id, assetId, documentId, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to approve asset", err)
		}
	}

	const handleReject = async (assetId: string): Promise<void> => {
		if (!documentId) return
		try {
			const response = await api.assets.reject(brand.id, assetId, documentId, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to reject asset", err)
		}
	}

	const handleConfigure = async (assetId: string, config: api.AssetConfigureRequest): Promise<void> => {
		if (!documentId) return
		try {
			const response = await api.assets.configure(brand.id, assetId, documentId, config, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to configure asset", err)
		}
	}

	const handleBulkSendToSettings = async (): Promise<void> => {
		if (!documentId) return
		const assetIds = Array.from(cached.selectedAssets)
		if (assetIds.length === 0) return

		// Filter to only configured assets
		const selectedAssets = assets.filter((a) => assetIds.includes(a.id))
		const configuredAssets = selectedAssets.filter((a) => a.configured_as)
		const unconfiguredAssets = selectedAssets.filter((a) => !a.configured_as)

		if (configuredAssets.length === 0) {
			setConfirmModalData({
				message: "No configured assets selected. Please configure assets before sending to settings.",
				onConfirm: () => setShowConfirmModal(false),
			})
			setShowConfirmModal(true)
			return
		}

		if (unconfiguredAssets.length > 0) {
			setConfirmModalData({
				message: `${unconfiguredAssets.length} asset(s) are not configured and will be skipped. Only ${configuredAssets.length} configured asset(s) will be sent to settings.`,
				onConfirm: async () => {
					setShowConfirmModal(false)
					try {
						await api.assets.bulkSendToSettings(
							brand.id,
							documentId,
							configuredAssets.map((a) => a.id),
							authzc.bearer(metaauthz)
						)
						cached.setSelectedAssets(new Set())
						await loadAssets()
					} catch (err) {
						console.error("unable to send to settings", err)
						setConfirmModalData({
							message: "Failed to send assets to settings. Please try again.",
							onConfirm: () => setShowConfirmModal(false),
						})
						setShowConfirmModal(true)
					}
				},
			})
			setShowConfirmModal(true)
			return
		}

		try {
			await api.assets.bulkSendToSettings(
				brand.id,
				documentId,
				configuredAssets.map((a) => a.id),
				authzc.bearer(metaauthz)
			)
			cached.setSelectedAssets(new Set())
			await loadAssets()
		} catch (err) {
			console.error("unable to send to settings", err)
			setConfirmModalData({
				message: "Failed to send assets to settings. Please try again.",
				onConfirm: () => setShowConfirmModal(false),
			})
			setShowConfirmModal(true)
		}
	}

	const handleShowTrainingModal = (): void => {
		const assetIds = Array.from(cached.selectedAssets)
		if (assetIds.length === 0) return
		setShowTrainingModal(true)
	}

	const handleSendToTraining = async (isOnBrand: boolean, destination: string): Promise<void> => {
		if (!documentId) return
		const assetIds = Array.from(cached.selectedAssets)
		if (assetIds.length === 0) return

		setShowTrainingModal(false)

		try {
			await api.assets.bulkSendToSettings(
				brand.id,
				documentId,
				assetIds,
				authzc.bearer(metaauthz),
				{
					send_to_training: true,
					is_on_brand: isOnBrand,
					training_destination: destination,
				}
			)
			cached.setSelectedAssets(new Set())
			await loadAssets()
		} catch (err) {
			console.error("unable to send to training", err)
			setConfirmModalData({
				message: "Failed to send assets to training. Please try again.",
				onConfirm: () => setShowConfirmModal(false),
			})
			setShowConfirmModal(true)
		}
	}

	if (!document) {
		return (
			<Container title="Loading..." urlgen={(bid) => routing.review(bid, documentId || "")}>
				<SubNavigation />
				<v2layouts.ContentArea>
					<div style={{ textAlign: "center", padding: "40px", color: "#757575" }}>Loading document...</div>
				</v2layouts.ContentArea>
			</Container>
		)
	}

	return (
		<Container title="Review Assets" urlgen={(bid) => routing.review(bid, documentId || "")}>
			<SubNavigation />

			<v2layouts.ContentArea>
				<div style={{ marginBottom: "20px" }}>
					<h4 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#212121" }}>
						{document.original_filename}
					</h4>
				</div>

				{document.parse_status === "failed" && (
					<v2layouts.ErrorMessage>
						Parsing failed: {document.parse_error || "Unknown error"}
					</v2layouts.ErrorMessage>
				)}

				{loading ? (
					<div style={{ textAlign: "center", padding: "40px", color: "#757575" }}>Loading assets...</div>
				) : assets.length === 0 ? (
					<v2layouts.EmptyState>
						<v2layouts.EmptyStateIcon>
							<icons.FileUpload />
						</v2layouts.EmptyStateIcon>
						<v2layouts.EmptyStateTitle>No assets found</v2layouts.EmptyStateTitle>
						<v2layouts.EmptyStateText>
							{document.parse_status === "completed"
								? "No assets were extracted from this document"
								: "Document has not been parsed yet"}
						</v2layouts.EmptyStateText>
					</v2layouts.EmptyState>
				) : (
					<>
						<AssetGrid
							assets={assets}
							selectedAssets={cached.selectedAssets}
							onSelectAsset={(id) => {
								const newSelected = new Set(cached.selectedAssets)
								if (newSelected.has(id)) {
									newSelected.delete(id)
								} else {
									newSelected.add(id)
								}
								cached.setSelectedAssets(newSelected)
							}}
							onApprove={handleApprove}
							onReject={handleReject}
							onConfigure={handleConfigure}
						/>
						{totalCount > pageSize && (
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginTop: "24px",
									padding: "16px",
									borderTop: "1px solid #e0e0e0",
								}}
							>
								<button
									onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
									disabled={currentPage === 1}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "8px 16px",
										backgroundColor: currentPage === 1 ? "#f5f5f5" : "#fff",
										border: "1px solid #e0e0e0",
										borderRadius: "4px",
										cursor: currentPage === 1 ? "not-allowed" : "pointer",
										color: currentPage === 1 ? "#9e9e9e" : "#212121",
										fontSize: "14px",
										fontWeight: 500,
									}}
								>
									{icons.chevrons.left({ disabled: currentPage === 1 })}
									Previous
								</button>
								<div style={{ fontSize: "14px", color: "#757575" }}>
									Page {currentPage} of {Math.ceil(totalCount / pageSize)} ({totalCount} total assets)
								</div>
								<button
									onClick={() => setCurrentPage((p) => p + 1)}
									disabled={currentPage >= Math.ceil(totalCount / pageSize)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "8px 16px",
										backgroundColor:
											currentPage >= Math.ceil(totalCount / pageSize) ? "#f5f5f5" : "#fff",
										border: "1px solid #e0e0e0",
										borderRadius: "4px",
										cursor: currentPage >= Math.ceil(totalCount / pageSize) ? "not-allowed" : "pointer",
										color: currentPage >= Math.ceil(totalCount / pageSize) ? "#9e9e9e" : "#212121",
										fontSize: "14px",
										fontWeight: 500,
									}}
								>
									Next
									{icons.chevrons.right({ disabled: currentPage >= Math.ceil(totalCount / pageSize) })}
								</button>
							</div>
						)}
					</>
				)}
			</v2layouts.ContentArea>

			{assets.length > 0 && (
				<BulkActions
					selectedCount={cached.selectedAssets.size}
					totalCount={assets.length}
					onSelectAll={() => cached.setSelectedAssets(new Set(assets.map((a) => a.id)))}
					onDeselectAll={() => cached.setSelectedAssets(new Set())}
					onSendToSettings={handleBulkSendToSettings}
					onSendToTraining={handleShowTrainingModal}
				/>
			)}

			{showConfirmModal && confirmModalData && (
				<ConfirmationModal
					title="Confirm Action"
					message={confirmModalData.message}
					onConfirm={confirmModalData.onConfirm}
					onCancel={() => setShowConfirmModal(false)}
				/>
			)}

			{showTrainingModal && (
				<TrainingModal
					title="Send to Training"
					assetCount={cached.selectedAssets.size}
					assetTypes={new Set(
						assets.filter((a) => cached.selectedAssets.has(a.id)).map((a) => a.asset_type)
					)}
					onConfirm={handleSendToTraining}
					onCancel={() => setShowTrainingModal(false)}
				/>
			)}
		</Container>
	)
}
